/**
 * Admin Blog Editor
 * Create/Edit blogs with AI generation support
 */
import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminApiGet, adminApiPost, adminApiPatch } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  ArrowLeft,
  Calendar,
  CircleNotch,
  Eye,
  FloppyDisk,
  Image as ImageIcon,
  Sparkle,
  Tag
} from '@phosphor-icons/react';
const BlogEditorNew = () => {
  const navigate = useNavigate();
  const { blogId } = useParams();
  const isEdit = !!blogId;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    read_time: '',
    published: false,
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    hero_image: '',
  });

  useEffect(() => {
    if (isEdit) {
      fetchBlog();
    }
  }, [blogId]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const blogs = await adminApiGet('/api/admin/blogs');
      const blog = blogs.find(b => b.id === blogId);
      if (blog) {
        setFormData({
          title: blog.title || '',
          slug: blog.slug || '',
          excerpt: blog.excerpt || '',
          content: blog.content || '',
          category: blog.category || '',
          read_time: blog.read_time || '',
          published: blog.published || false,
          meta_title: blog.meta_title || '',
          meta_description: blog.meta_description || '',
          meta_keywords: blog.meta_keywords || '',
          hero_image: blog.hero_image || '',
        });
      } else {
        toast.error('Blog not found');
        navigate('/admin/blogs');
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      toast.error('Failed to load blog');
      navigate('/admin/blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from title
    if (field === 'title' && !isEdit) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleAIGenerate = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title first');
      return;
    }

    try {
      setGenerating(true);
      const data = await adminApiPost('/api/admin/blogs/generate', { title: formData.title });
      
      setFormData(prev => ({
        ...prev,
        excerpt: data.excerpt || prev.excerpt,
        content: data.content || prev.content,
        category: data.category || prev.category,
        read_time: data.read_time || prev.read_time,
        meta_title: data.meta_title || prev.meta_title,
        meta_description: data.meta_description || prev.meta_description,
        meta_keywords: data.meta_keywords || prev.meta_keywords,
      }));
      
      toast.success('AI content generated successfully!');
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error(error.message || 'AI generation failed. Check if OpenAI API key is configured.');
    } finally {
      setGenerating(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    try {
      setUploadingImage(true);
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: uploadFormData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      // Construct full URL for the image
      const imageUrl = data.file_url || data.url;
      const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${BACKEND_URL}/api${imageUrl}`;
      
      setFormData(prev => ({
        ...prev,
        hero_image: fullImageUrl,
      }));
      toast.success('Hero image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload hero image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async (publish = false) => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        published: publish ? true : formData.published,
      };

      if (isEdit) {
        await adminApiPatch(`/api/admin/blogs/${blogId}`, payload);
        toast.success('Blog updated successfully');
      } else {
        await adminApiPost('/api/admin/blogs', payload);
        toast.success('Blog created successfully');
      }

      navigate('/admin/blogs');
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error(error.message || 'Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="h-96 bg-white/10 rounded-xl animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/blogs')}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">{isEdit ? 'Edit Blog' : 'New Blog Post'}</h1>
            <p className="text-gray-400">
              {isEdit ? 'Update your blog post' : 'Create a new blog post with AI assistance'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <CircleNotch size={18} className="animate-spin" /> : <FloppyDisk size={18} />}
            Save Draft
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving}
            className="px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2 font-semibold"
          >
            {saving ? <CircleNotch size={18} className="animate-spin" /> : <Eye size={18} />}
            Publish
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title & AI Generate */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter blog title..."
                className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
              />
              <button
                onClick={handleAIGenerate}
                disabled={generating || !formData.title.trim()}
                className="px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2 font-semibold whitespace-nowrap"
              >
                {generating ? (
                  <>
                    <CircleNotch size={18} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkle size={18} />
                    AI Generate
                  </>
                )}
              </button>
            </div>
            {generating && (
              <p className="text-xs text-purple-400 mt-2">AI is crafting your blog content...</p>
            )}
          </div>

          {/* Slug */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">URL Slug</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => handleChange('slug', e.target.value)}
              placeholder="blog-post-url"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          {/* Excerpt */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Excerpt</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => handleChange('excerpt', e.target.value)}
              placeholder="Brief summary for cards and previews..."
              rows={3}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50 resize-none"
            />
          </div>

          {/* Content */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Content *</label>
            <textarea
              value={formData.content}
              onChange={(e) => handleChange('content', e.target.value)}
              placeholder="Write your blog content here... (Markdown supported)"
              rows={15}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50 resize-none font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-2">
              Markdown formatting supported: **bold**, *italic*, [link](url), etc.
            </p>
          </div>

          {/* SEO Meta */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">SEO Metadata</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Meta Title</label>
                <input
                  type="text"
                  value={formData.meta_title}
                  onChange={(e) => handleChange('meta_title', e.target.value)}
                  placeholder="SEO-optimized title (max 60 chars)"
                  maxLength={60}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Meta Description</label>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => handleChange('meta_description', e.target.value)}
                  placeholder="SEO description (max 160 chars)"
                  maxLength={160}
                  rows={3}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Keywords</label>
                <input
                  type="text"
                  value={formData.meta_keywords}
                  onChange={(e) => handleChange('meta_keywords', e.target.value)}
                  placeholder="keyword1, keyword2, keyword3"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Category & Meta */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  placeholder="AI & Technology"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Read Time</label>
                <input
                  type="text"
                  value={formData.read_time}
                  onChange={(e) => handleChange('read_time', e.target.value)}
                  placeholder="5 min read"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
                />
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Hero Image</h3>
            
            {formData.hero_image && (
              <img
                src={formData.hero_image}
                alt="Hero preview"
                className="w-full h-48 object-cover rounded-lg mb-4"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}

            <label className="block w-full cursor-pointer">
              <div className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center hover:border-[#6366F1]/50 transition-colors">
                {uploadingImage ? (
                  <div className="flex flex-col items-center gap-2">
                    <CircleNotch size={24} className="animate-spin text-[#6366F1]" />
                    <p className="text-xs text-gray-400">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <ImageIcon size={24} className="text-gray-400" />
                    <p className="text-sm text-white">Upload Hero Image</p>
                    <p className="text-xs text-gray-500">Click to upload or drag and drop</p>
                  </div>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={uploadingImage}
              />
            </label>
            
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1">Or paste image URL:</p>
              <input
                type="text"
                value={formData.hero_image}
                onChange={(e) => handleChange('hero_image', e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
              />
            </div>
          </div>

          {/* Status */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Status</h3>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Published</span>
              <button
                onClick={() => handleChange('published', !formData.published)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.published ? 'bg-[#6366F1]' : 'bg-white/20'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.published ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogEditorNew;
